/**
 * Subscription display constants — label/style maps shared across the
 * admin subscriptions UI (BillingSubscriptions, SubscriptionDetailModal,
 * TenantDetails Subscriptions tab).
 *
 * Extracted from `SubscriptionDetailModal.tsx` per H-ARCH-2 (separation of
 * pure constants from React component).
 *
 * @see docs/specifications/2026-05-14-tenant-detail-billing-tabs/reviews/architecture-reviewer-review.md
 */

export const BILLING_INTERVAL_LABELS: Record<string, string> = {
  monthly: "miesięczny",
  quarterly: "kwartalny",
  yearly: "roczny",
};

/**
 * Status badge styling map.
 *
 * SUPERSEDED + GRACE added per AC-C6 — historical chain entries and
 * grace-period subscriptions need distinct visual treatment.
 *
 * SCHEDULED added per AC-8c (docs/specifications/2026-07-15-scheduled-subscription-succession/spec.md)
 * — indigo is distinct from ACTIVE (green) and TRIALING (sky) so admins can
 * tell a not-yet-active successor apart from a live subscription.
 */
export const STATUS_BADGE_STYLES: Record<string, string> = {
  ACTIVE: "bg-green-100 text-green-800",
  TRIALING: "bg-sky-100 text-sky-800",
  EXPIRED: "bg-neutral-100 text-neutral-600",
  CANCELED: "bg-red-100 text-red-800",
  PAST_DUE: "bg-amber-100 text-amber-800",
  GRACE: "bg-orange-100 text-orange-800",
  SUPERSEDED: "bg-gray-100 text-gray-600",
  SCHEDULED: "bg-indigo-100 text-indigo-800",
};

export const STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Aktywna",
  TRIALING: "Okres próbny",
  EXPIRED: "Wygasła",
  CANCELED: "Anulowana",
  PAST_DUE: "Zaległa płatność",
  GRACE: "Okres karencji",
  SUPERSEDED: "Zastąpiona",
  SCHEDULED: "Zaplanowana",
};

export const FEATURE_CODE_LABELS: Record<string, string> = {
  "config:billing_interval": "Interwał rozliczeniowy",
  "config:target_billing_interval": "Docelowy interwał (po trialu)",
  "config:max_seats": "Maks. stanowiska",
};

/**
 * SLO buffer for the SubscriptionSuccessionJob (hourly cron, `:15` offset —
 * spec.md "SLO przełączenia"): normal switchover completes within ~30 min
 * average, ~60 min worst case; the ops alert only fires past 2h (AC-12).
 *
 * Without a buffer, `isOverdueScheduled` would flag every SCHEDULED sub in
 * that healthy 0-60min window as amber "opóźniona" (MEDIUM UX finding,
 * consolidated.md "Badge 'Zaplanowana (opóźniona)' odpala się natychmiast
 * po granicy okresu, nie po SLO joba") — so the threshold sits just above
 * the worst-case normal run, well below the 2h alert.
 */
export const OVERDUE_SCHEDULED_BUFFER_MINUTES = 75;

/**
 * AC-8d: SCHEDULED whose period started more than the SLO buffer ago but
 * the succession job hasn't switched it to ACTIVE yet — flag it visually
 * for admins.
 *
 * Shared by SubscriptionDetailModal and SubscriptionRow so the amber
 * "Zaplanowana (opóźniona)" badge is consistent between the detail modal
 * and the list/row views (docs/specifications/2026-07-15-scheduled-subscription-succession/reviews/consolidated.md,
 * HIGH "Badge 'Zaplanowana (opóźniona)' istnieje wyłącznie w modalu").
 */
export function isOverdueScheduled(sub: {
  status: string;
  currentPeriodStart?: string | null;
}): boolean {
  if (sub.status !== "SCHEDULED" || !sub.currentPeriodStart) return false;
  const bufferMs = OVERDUE_SCHEDULED_BUFFER_MINUTES * 60 * 1000;
  return new Date(sub.currentPeriodStart).getTime() + bufferMs < Date.now();
}
